import React from 'react';
import './AddPage.scss';
import { Button, Card, Checkbox, Dropdown, Icon, Input, Textarea, Typography } from '@design-system/index';
import { Formik } from 'formik';
import { MemberToCreate, MemberTable } from '@services/member/member/member.types';
import { Plus, Delete, MembersListLine } from '@icons';
import { useAddPageVM } from './useAddPageVM';
import { useParams } from 'react-router-dom';
import {
    useGetOrganizationCodesMutation,
    useGetStateCodesMutation,
    useGetServiceCodesMutation
} from '@services/member/list/list.endpoints';
import { FileUpload } from '@components/FileUpload/FileUpload';
import { CdnFile } from '@services/core/cdn/file/file';
import { AutocompleteInput } from '@components/AutocompleteInput/AutocompleteInput';
import { AsyncDropdown, DropdownOption } from '@design-system/components/Dropdown/AsyncDropdown';
import { useGetMembersMutation } from '@services/member/member/member.endpoints';
import { CONTRIBUTION_TYPE, FAMILY_STATUS, GENDER } from '@utils/enumTranslation';

export default function AddPage() {
    const { id } = useParams();
    const { submitForm, isLoading, member, isEdit } = useAddPageVM();

    return (
        <div className="add-member-container">
            <Card
                isLoading={isLoading}
                headerType="full"
                showBack
                headerTitle={isEdit ? 'Modification adhérent' : 'Création nouvel adhérent'}
                headerIcon={<Icon icon={<MembersListLine />} size={20} color="primary" />}
            >
                <>
                    <Formik
                        key={id ?? 'new'}
                        initialValues={
                            member || {
                                administrativeInformation: {
                                    registrationNumber: '',
                                    oldRegistrationNumber: '',
                                    stateCode: '',
                                    serviceCode: '',
                                    organization: '',
                                    contributionType: '',
                                    registeredAt: ''
                                },
                                personalInformation: {
                                    lastname: '',
                                    firstname: '',
                                    birthPlace: '',
                                    birthName: '',
                                    gender: '',
                                    birthDate: '',
                                    familyStatus: ''
                                },
                                contactInformation: {
                                    address: '',
                                    additionalAddress: '',
                                    city: '',
                                    zipCode: '',
                                    phone: '',
                                    email: '',
                                    isEmailExisting: true
                                },
                                documentsIds: [],
                                documents: [],
                                observation: '',
                                family: {
                                    spouse: {
                                        lastname: '',
                                        firstname: '',
                                        birthName: '',
                                        birthDate: '',
                                        gender: '',
                                        cosemMember: false
                                    },
                                    observation: ''
                                },
                                children: []
                            }
                        }
                        enableReinitialize
                        onSubmit={async (values: MemberToCreate) => {
                            await submitForm(values);
                        }}
                    >
                        {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting }) => {

                            const handleNoEmail = () => {
                                if (values.contactInformation.isEmailExisting) {
                                    setFieldValue('contactInformation.isEmailExisting', false);
                                    if (values.personalInformation.lastname.length > 0 && values.personalInformation.firstname.length > 0) {
                                        setFieldValue('contactInformation.email', 'no-email-' + values.personalInformation.lastname + '-' + values.personalInformation.firstname + '@gmail.com');
                                    } else {
                                        const randomLastnameNumber = Math.floor(Math.random() * 1000000) + 1;
                                        const randomFirstnameNumber = Math.floor(Math.random() * 1000000) + 1;
                                        setFieldValue(
                                            'contactInformation.email',
                                            `no-email-${randomLastnameNumber}-${randomFirstnameNumber}@gmail.com`
                                        );
                                    }
                                } else {
                                    setFieldValue('contactInformation.isEmailExisting', true);
                                    setFieldValue('contactInformation.email', '');
                                }
                            }

                            return (
                                <form onSubmit={handleSubmit} className="form">
                                    <div className="form-section-container">
                                        <div className="form-section" id="administrative-section">
                                            <Typography intrinsic="h3">Informations administratives</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            label="Matricule"
                                                            name="administrativeInformation.registrationNumber"
                                                            value={values.administrativeInformation.registrationNumber}
                                                            required
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    {values.administrativeInformation.contributionType == 'retired' && (
                                                        <div className="form-group">
                                                            <Input
                                                                label="Ancien matricule"
                                                                name="administrativeInformation.oldRegistrationNumber"
                                                                value={values.administrativeInformation.oldRegistrationNumber}
                                                                required
                                                                onChange={handleChange}
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="form-group">
                                                        <Dropdown
                                                            label="Organisme"
                                                            isSearchable
                                                            mutationHook={useGetOrganizationCodesMutation}
                                                            required
                                                            value={values.administrativeInformation.organization}
                                                            onOptionChange={(option) =>
                                                                setFieldValue('administrativeInformation.organization', option.id)
                                                            }
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Dropdown
                                                            isSearchable
                                                            label="Service"
                                                            isClearable
                                                            mutationHook={useGetServiceCodesMutation}
                                                            value={values.administrativeInformation.serviceCode}
                                                            onOptionChange={(option) =>
                                                                setFieldValue('administrativeInformation.serviceCode', option?.id)
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Dropdown
                                                            label="Code état"
                                                            isSearchable
                                                            mutationHook={useGetStateCodesMutation}
                                                            required
                                                            value={values.administrativeInformation.stateCode}
                                                            onOptionChange={(option) =>
                                                                setFieldValue('administrativeInformation.stateCode', option.id)
                                                            }
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Dropdown
                                                            label="Type de cotisation"
                                                            required
                                                            isSearchable
                                                            options={Object.entries(CONTRIBUTION_TYPE).map(([key, value]) => ({
                                                                id: key,
                                                                label: value
                                                            }))}
                                                            value={values.administrativeInformation.contributionType}
                                                            onOptionChange={(option) =>
                                                                setFieldValue('administrativeInformation.contributionType', option.id)
                                                            }
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            type="date"
                                                            required
                                                            name="administrativeInformation.registeredAt"
                                                            label="Date d'adhésion"
                                                            value={values.administrativeInformation.registeredAt}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            type="date"
                                                            name="administrativeInformation.unregisteredAt"
                                                            label="Date de sortie"
                                                            value={values.administrativeInformation.unregisteredAt ?? ''}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section" id="personal-section">
                                            <Typography intrinsic="h3">Informations personnelles</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            label="Nom"
                                                            required
                                                            name="personalInformation.lastname"
                                                            value={values.personalInformation.lastname}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            label="Prénom"
                                                            required
                                                            name="personalInformation.firstname"
                                                            value={values.personalInformation.firstname}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            label="Nom de naissance"
                                                            name="personalInformation.birthName"
                                                            value={values.personalInformation.birthName}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            label="Lieu de naissance"
                                                            name="personalInformation.birthPlace"
                                                            value={values.personalInformation.birthPlace}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            type="date"
                                                            required
                                                            name="personalInformation.birthDate"
                                                            label="Date de naissance"
                                                            value={values.personalInformation.birthDate}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Dropdown
                                                            label="Genre"
                                                            required
                                                            options={Object.entries(GENDER).map(([key, value]) => ({
                                                                id: key,
                                                                label: value
                                                            }))}
                                                            value={values.personalInformation.gender}
                                                            onOptionChange={(option) =>
                                                                setFieldValue('personalInformation.gender', option.id)
                                                            }
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Dropdown
                                                            label="Situation familiale"
                                                            isClearable
                                                            options={Object.entries(FAMILY_STATUS).map(([key, value]) => ({
                                                                id: key,
                                                                label: value
                                                            }))}
                                                            value={values.personalInformation.familyStatus}
                                                            onOptionChange={(option) =>
                                                                setFieldValue('personalInformation.familyStatus', option?.id ?? '')
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section" id="contact-section">
                                            <Typography intrinsic="h3">Coordonnées</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            label="N° Téléphone"
                                                            required
                                                            type="tel"
                                                            name="contactInformation.phone"
                                                            value={values.contactInformation.phone}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        {values.contactInformation.isEmailExisting && (
                                                            <Input
                                                                required
                                                                label="Adresse email"
                                                                type="email"
                                                                name="contactInformation.email"
                                                                value={values.contactInformation.email}
                                                                onChange={handleChange}
                                                            />
                                                        )}
                                                        <Checkbox
                                                            checked={!values.contactInformation.isEmailExisting}
                                                            onChange={() => { handleNoEmail() }}
                                                        >
                                                            <Typography intrinsic="p" variant="p2" color="grey1">
                                                                L'adhérent n'a pas d'adresse email
                                                            </Typography>
                                                        </Checkbox>
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <AutocompleteInput
                                                            onPlaceSelect={(place) => {
                                                                setFieldValue('contactInformation.address', place.address);
                                                                setFieldValue('contactInformation.city', place.city);
                                                                setFieldValue('contactInformation.zipCode', place.postal_code);
                                                            }}
                                                        >
                                                            <Input
                                                                label="Adresse"
                                                                required
                                                                name="contactInformation.address"
                                                                value={values.contactInformation.address}
                                                                onChange={handleChange}
                                                            />
                                                        </AutocompleteInput>
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            label="Complément d'adresse"
                                                            name="contactInformation.additionalAddress"
                                                            value={values.contactInformation.additionalAddress}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Input
                                                            label="Ville"
                                                            required
                                                            name="contactInformation.city"
                                                            value={values.contactInformation.city}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <Input
                                                            label="Code postal"
                                                            required
                                                            name="contactInformation.zipCode"
                                                            value={values.contactInformation.zipCode}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section" id="documents-section">
                                            <Typography intrinsic="h3">Documents</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <FileUpload
                                                            width="100%"
                                                            height="214px"
                                                            isMultiple={true}
                                                            isImage={false}
                                                            files={values.documents}
                                                            onSuccess={(files: CdnFile[]) => {
                                                                setFieldValue(
                                                                    'documentsIds',
                                                                    files.map((file) => file.id)
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section" id="comment-section">
                                            <Typography intrinsic="h3">Commentaires</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <Textarea
                                                            label="Ajouter un commentaire"
                                                            name="observation"
                                                            value={values.observation}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-section" id="family-section">
                                            <Typography intrinsic="h3">Famille</Typography>
                                            <div className="form-section-fields">
                                                <div className="form-multi-line">
                                                    <div className="form-line">
                                                        <Typography intrinsic="p">Conjoint</Typography>
                                                    </div>

                                                    <div className="form-line">
                                                        <Checkbox
                                                            checked={!!values.family.spouse.cosemMember}
                                                            onChange={(v) => setFieldValue('family.spouse.cosemMember', v)}
                                                        >
                                                            <Typography intrinsic="p" variant="p2" color="grey1">
                                                                Adhérent(e) COSEM
                                                            </Typography>
                                                        </Checkbox>
                                                    </div>

                                                    {values.family.spouse.cosemMember ? (
                                                        <div className="form-line">
                                                            <div className="form-group">
                                                                <AsyncDropdown<MemberTable>
                                                                    label="Rechercher un adhérent"
                                                                    onOptionChange={(o) => {
                                                                        setFieldValue('selectedSpouse', o);
                                                                    }}
                                                                    mutationHook={useGetMembersMutation}
                                                                    mapDataToOptions={(data) =>
                                                                        data.map((item) => ({
                                                                            id: item.id.toString(),
                                                                            label:
                                                                                item.cosemNumber +
                                                                                ' - ' +
                                                                                item.personalInformation.lastname +
                                                                                ' ' +
                                                                                item.personalInformation.firstname
                                                                        }))
                                                                    }
                                                                    value={values.selectedSpouse as DropdownOption}
                                                                    isSearchable
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="form-line">
                                                                <div className="form-group">
                                                                    <Input
                                                                        label="Nom"
                                                                        name="family.spouse.lastname"
                                                                        value={values.family.spouse.lastname}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>
                                                                <div className="form-group">
                                                                    <Input
                                                                        label="Prénom"
                                                                        name="family.spouse.firstname"
                                                                        value={values.family.spouse.firstname}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="form-line">
                                                                <div className="form-group">
                                                                    <Input
                                                                        label="Nom de naissance"
                                                                        name="family.spouse.birthName"
                                                                        value={values.family.spouse.birthName}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>
                                                                <div className="form-group">
                                                                    <Input
                                                                        type="date"
                                                                        label="Date de naissance"
                                                                        name="family.spouse.birthDate"
                                                                        value={values.family.spouse.birthDate}
                                                                        onChange={handleChange}
                                                                    />
                                                                </div>
                                                                <div className="form-group">
                                                                    <Dropdown
                                                                        label="Genre"
                                                                        options={Object.entries(GENDER).map(([key, value]) => ({
                                                                            id: key,
                                                                            label: value
                                                                        }))}
                                                                        value={values.family.spouse.gender}
                                                                        onOptionChange={(option) =>
                                                                            setFieldValue('family.spouse.gender', option.id)
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>

                                                {values.children &&
                                                    values.children.map((child, index) => (
                                                        <React.Fragment key={`child-${index}`}>
                                                            <div className="form-multi-line">
                                                                <div className="form-line" style={{ gap: '14px' }}>
                                                                    <Button
                                                                        size="small"
                                                                        color="grey"
                                                                        icon={<Icon icon={<Delete />} size={12} />}
                                                                        type="button"
                                                                        bordered
                                                                        onClick={() => {
                                                                            const newChildren = values.children.filter(
                                                                                (_, i) => i !== index
                                                                            );
                                                                            setFieldValue('children', newChildren);
                                                                        }}
                                                                    />
                                                                    <Typography intrinsic="p">Enfant N°{index + 1}</Typography>
                                                                </div>

                                                                <div className="form-line">
                                                                    <div className="form-group">
                                                                        <Input
                                                                            label="Nom"
                                                                            name={`children[${index}].lastname`}
                                                                            value={child.lastname}
                                                                            onChange={handleChange}
                                                                        />
                                                                    </div>
                                                                    <div className="form-group">
                                                                        <Input
                                                                            label="Prénom"
                                                                            name={`children[${index}].firstname`}
                                                                            value={child.firstname}
                                                                            onChange={handleChange}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="form-line">
                                                                    <div className="form-group">
                                                                        <Input
                                                                            type="date"
                                                                            label="Date de naissance"
                                                                            name={`children[${index}].birthDate`}
                                                                            value={child.birthDate}
                                                                            onChange={handleChange}
                                                                        />
                                                                    </div>
                                                                    <div className="form-group">
                                                                        <Dropdown
                                                                            label="Genre"
                                                                            options={Object.entries(GENDER).map(([key, value]) => ({
                                                                                id: key,
                                                                                label: value
                                                                            }))}
                                                                            value={child.gender}
                                                                            onOptionChange={(option) =>
                                                                                setFieldValue(`children[${index}].gender`, option.id)
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="form-line">
                                                                    <div className="form-group checkbox-container">
                                                                        <Checkbox
                                                                            checked={!!child.dependent}
                                                                            onChange={(v) =>
                                                                                setFieldValue(`children[${index}].dependent`, v)
                                                                            }
                                                                        >
                                                                            <Typography intrinsic="p" variant="p2" color="grey1">
                                                                                À charge
                                                                            </Typography>
                                                                        </Checkbox>
                                                                    </div>
                                                                    <div className="form-group checkbox-container">
                                                                        <Checkbox
                                                                            checked={!!child.deceased}
                                                                            onChange={(v) =>
                                                                                setFieldValue(`children[${index}].deceased`, v)
                                                                            }
                                                                        >
                                                                            <Typography intrinsic="p" variant="p2" color="grey1">
                                                                                {' '}
                                                                                Décédé(e)
                                                                            </Typography>
                                                                        </Checkbox>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </React.Fragment>
                                                    ))}

                                                <div className="form-multi-line">
                                                    <div className="form-line" style={{ gap: '14px' }}>
                                                        <Button
                                                            size="small"
                                                            color="grey"
                                                            icon={<Icon icon={<Plus />} size={12} />}
                                                            type="button"
                                                            bordered
                                                            onClick={() => {
                                                                setFieldValue('children', [
                                                                    ...values.children,
                                                                    {
                                                                        lastname: '',
                                                                        firstname: '',
                                                                        birthDate: '',
                                                                        gender: '',
                                                                        dependent: true,
                                                                        deceased: false
                                                                    }
                                                                ]);
                                                            }}
                                                        />
                                                        <Typography intrinsic="p">Enfant N°{values.children.length + 1}</Typography>
                                                    </div>
                                                </div>

                                                <div className="form-line">
                                                    <Textarea
                                                        label="Ajouter un commentaire"
                                                        name="family.observation"
                                                        value={values.family.observation}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="action-buttons">
                                        <Button type="submit" disabled={isSubmitting} variant="primary">
                                            {isEdit ? 'Valider la modification' : 'Valider la création'}
                                        </Button>
                                    </div>
                                </form>
                            );
                        }}
                    </Formik>
                </>
            </Card>
        </div>
    );
}
